// frontend/packages/business-console/src/components/crm/AddCustomerDialog.jsx
//
// Standalone "Add customer" dialog for the CRM list page. The quick-create
// form already existed inside the estimate flow's CustomerPicker, but there
// was no way to add a customer directly from the CRM page. This reuses the
// same `createCustomer` service so both paths stay consistent.

import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { createCustomer } from '../../services/crmService';

export default function AddCustomerDialog({ open, onClose, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setError('');
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose?.();
  };

  const canSave =
    firstName.trim() && lastName.trim() && email.trim() && !saving;

  const submit = async () => {
    setError('');
    setSaving(true);
    try {
      const created = await createCustomer({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
      });
      reset();
      onCreated?.(created);
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Couldn't add customer.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>Add customer</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              required
            />
          </Stack>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          {saving ? 'Saving' : 'Add customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
